import { type QueryClient, useQueryClient } from '@tanstack/vue-query'
import { computed, ref } from 'vue'
import { useHttp, useTranslate } from '@/shared/lib'
import { useNotification } from '@/shared/ui'
import { PASSPORT_RUNTIME_LIST_TABLE_QUERY_KEY } from './passportRuntimeListTableQueryKeys'

export { useQueryClient }

/** Body for `POST .../table-bindings/link` and `.../unlink`. */
export type TableBindingLinkBody = {
  list_group_id: number
  child_object_list_id: number
  table_field_id?: string
}

function inventoryMutationErrorMessage(error: unknown, trs: (key: string) => string): string {
  const e = error as { response?: { data?: { data?: { msg?: string }; error?: string } } }
  return trs(
    e?.response?.data?.data?.msg ?? e?.response?.data?.error ?? 'request.unknown_error',
  )
}

/**
 * After link, unlink, or delete-child, refresh LIST table rows and parent passport payloads.
 */
export async function invalidatePassportRuntimeListTableQueries(
  queryClient: QueryClient,
  /** Route map group id (`object_id`) — matches query key segment after `PASSPORT_RUNTIME_LIST_TABLE_QUERY_KEY`. */
  inventoryGroupObjectId: string,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: [PASSPORT_RUNTIME_LIST_TABLE_QUERY_KEY, inventoryGroupObjectId],
    }),
    queryClient.invalidateQueries({ queryKey: ['load_invent_list_item'] }),
  ])
}

/**
 * Table binding mutations (`link` / `unlink` / `delete-child`) for passport LIST tables.
 * Surfaces API errors via `useNotification` (same pattern as `useRemoveInventoryObject`).
 */
export function useTableBindingMutations() {
  const http = useHttp()
  const { addError } = useNotification()
  const { trs } = useTranslate()

  /** Счётчик активных запросов: корректно учитывает параллельные вызовы (link/unlink/delete). */
  const inFlight = ref(0)
  /** Признак выполнения любой из табличных мутаций — для блокировки UI на время запроса. */
  const isPending = computed(() => inFlight.value > 0)

  /**
   * Выполняет переданный запрос, удерживая `isPending` на время выполнения.
   * Ошибку показывает через `useNotification` и возвращает `false`.
   * @param request функция, отправляющая запрос
   */
  async function withPending(request: () => Promise<unknown>): Promise<boolean> {
    inFlight.value++
    try {
      await request()
      return true
    } catch (e) {
      addError(inventoryMutationErrorMessage(e, trs))
      return false
    } finally {
      inFlight.value--
    }
  }

  function postLink(
    inventoryGroupObjectId: string,
    parentObjectListId: number,
    body: TableBindingLinkBody,
  ): Promise<boolean> {
    const g = encodeURIComponent(inventoryGroupObjectId.trim())
    return withPending(() =>
      http.post(
        `/inventory/objects/${g}/passport/${parentObjectListId}/table-bindings/link`,
        body,
      ),
    )
  }

  function postUnlink(
    inventoryGroupObjectId: string,
    parentObjectListId: number,
    body: TableBindingLinkBody,
  ): Promise<boolean> {
    const g = encodeURIComponent(inventoryGroupObjectId.trim())
    return withPending(() =>
      http.post(
        `/inventory/objects/${g}/passport/${parentObjectListId}/table-bindings/unlink`,
        body,
      ),
    )
  }

  function postDeleteChild(
    inventoryGroupObjectId: string,
    childObjectListId: number,
  ): Promise<boolean> {
    const g = encodeURIComponent(inventoryGroupObjectId.trim())
    return withPending(() =>
      http.post(`/inventory/objects/${g}/passport/table-bindings/delete-child`, {
        child_object_list_id: childObjectListId,
      }),
    )
  }

  return {
    isPending,
    postLink,
    postUnlink,
    postDeleteChild,
  }
}
